import { useState, useEffect } from "react";
import axios from "axios";
import {
  Mail,
  FileCheck,
  MessageSquare,
  Send,
  Calendar,
  Copy,
  Check,
  ChevronRight,
  ChevronDown,
  Gamepad2,
  Loader2,
  Plus,
  X,
  Search,
  HelpCircle,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const categoryIcons = {
  acknowledgment: FileCheck,
  clarification: MessageSquare,
  quote: Send,
  followup: Mail,
  appointment: Calendar,
  animation_info: Gamepad2
};

const categoryLabels = {
  acknowledgment: "Accuse de reception",
  clarification: "Demande de precisions",
  quote: "Envoi de devis",
  followup: "Relance",
  appointment: "Prise de rendez-vous",
  animation_info: "Fiches animations"
};

// Detect what kind of pricing parameter an animation uses
const detectPricingType = (pricing) => {
  if (!pricing || Object.keys(pricing).length === 0) return null;
  const keys = Object.keys(pricing);
  const joined = keys.join(" ").toLowerCase();
  if (joined.includes("equipe")) return { type: "equipes", label: "Combien d'equipes sont prevues ?" };
  if (joined.includes("pax") || joined.includes("personnes") || joined.includes("joueurs"))
    return { type: "participants", label: "Combien de participants sont prevus ?" };
  if (joined.includes("journee") || joined.includes("h00") || joined.includes("heure") || joined.includes("duree"))
    return { type: "duree", label: "Quelle est la duree de l'animation ?" };
  if (joined.includes("table") || joined.includes("stand") || joined.includes("jeux"))
    return { type: "quantite", label: "Combien en souhaitez-vous ?" };
  return null;
};

// Find the best matching tier for a given count
const findBestTier = (pricing, count) => {
  if (!pricing || !count) return Object.keys(pricing || {})[0] || null;
  const keys = Object.keys(pricing);
  let bestKey = keys[0];
  let bestDist = Infinity;
  for (const key of keys) {
    const nums = key.match(/\d+/g);
    if (nums) {
      for (const n of nums) {
        const dist = Math.abs(parseInt(n) - count);
        if (dist < bestDist) {
          bestDist = dist;
          bestKey = key;
        }
      }
    }
  }
  return bestKey;
};

// Format any date string (ISO YYYY-MM-DD, DD/MM/YYYY, French text) to DD/MM/YYYY.
const formatDateFR = (dateStr) => {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  // Already DD/MM/YYYY
  const fr = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (fr) return `${fr[1].padStart(2, '0')}/${fr[2].padStart(2, '0')}/${fr[3]}`;
  // ISO YYYY-MM-DD
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  // French textual date "15 juillet 2026"
  const months = { janvier: '01', fevrier: '02', 'février': '02', mars: '03', avril: '04', mai: '05', juin: '06', juillet: '07', aout: '08', 'août': '08', septembre: '09', octobre: '10', novembre: '11', decembre: '12', 'décembre': '12' };
  const m = s.toLowerCase().match(/(\d{1,2})\s+(janvier|fevrier|février|mars|avril|mai|juin|juillet|aout|août|septembre|octobre|novembre|decembre|décembre)\s+(\d{4})/);
  if (m) return `${m[1].padStart(2, '0')}/${months[m[2]]}/${m[3]}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }
  return s;
};

const TemplateSelector = ({ templates, extractedData, senderEmail, apiUrl, isOutlookContext, createdQuote }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [renderedContent, setRenderedContent] = useState({ subject: "", body: "", bodyHtml: "" });
  const [variables, setVariables] = useState({});
  const [copied, setCopied] = useState(false);
  const [loadingDynamic, setLoadingDynamic] = useState(false);

  // Animation selector state
  const [showAnimSelector, setShowAnimSelector] = useState(false);
  const [allAnimations, setAllAnimations] = useState([]);
  const [selectedAnimations, setSelectedAnimations] = useState([]);
  const [animSearch, setAnimSearch] = useState("");
  const [pricingQuestion, setPricingQuestion] = useState(null);
  const [pricingAnswer, setPricingAnswer] = useState("");
  const [pendingAnimIndex, setPendingAnimIndex] = useState(null);

  // Group templates by category
  const groupedTemplates = templates.reduce((acc, template) => {
    if (!acc[template.category]) acc[template.category] = [];
    acc[template.category].push(template);
    return acc;
  }, {});

  // Load all animations for the catalog selector
  const loadAnimations = async () => {
    try {
      const res = await axios.get(`${apiUrl}/animations`);
      setAllAnimations(res.data);
    } catch (e) {
      console.error("Error loading animations:", e);
    }
  };

  // Resolve the value for a placeholder name from extracted data + created quote.
  // Shared by both the standard and the animation_info rendering paths.
  const valueForVar = (varName) => {
    switch (varName) {
      case 'first_name': return extractedData?.first_name || '';
      case 'last_name': return extractedData?.last_name || '';
      case 'company': return extractedData?.company || '';
      case 'event_date': return formatDateFR(extractedData?.event_date);
      case 'event_end_date': return formatDateFR(extractedData?.event_end_date);
      case 'event_location': return extractedData?.event_location || '';
      case 'event_horaires': return extractedData?.event_horaires || '';
      case 'participants_count': return extractedData?.participants_count?.toString() || '';
      case 'animations': return extractedData?.requested_animations?.join(', ') || '';
      case 'lien_devis': return createdQuote?.public_link || '';
      case 'total': return createdQuote?.total != null ? String(createdQuote.total) : '';
      default: return '';
    }
  };

  // Find every {{placeholder}} present in a template's subject + body.
  const extractPlaceholders = (template) => {
    const fullText = `${template.subject || ''} ${template.body || ''}`;
    return Array.from(
      new Set([...fullText.matchAll(/\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi)].map((m) => m[1]))
    );
  };

  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);

    // Special handling for animation_info template: the variant for each
    // animation has already been picked in the Aperçu/Opportunité view, so we
    // call the backend directly with the resolved Sellsy references and
    // render the email body inline — no dialog needed.
    if (template.category === "animation_info") {
      const refs = extractedData?.requested_animations || [];
      // Resolve every other placeholder (company, event_date, lien_devis, ...)
      // so the backend can substitute them alongside {{animation_links}}.
      const extraVars = {};
      extractPlaceholders(template).forEach((name) => {
        if (name === 'animation_links' || name === 'first_name') return;
        extraVars[name] = valueForVar(name);
      });
      setLoadingDynamic(true);
      try {
        const res = await axios.post(`${apiUrl}/email-templates/generate-animation-links`, {
          animation_references: refs,
          first_name: extractedData?.first_name || "Client",
          variables: extraVars,
        });
        setRenderedContent({
          subject: res.data.subject,
          body: res.data.body,
          bodyHtml: res.data.body_html || res.data.body,
        });
      } catch (e) {
        console.error("Error generating animation_info email:", e);
        toast.error("Erreur lors de la génération du mail");
      } finally {
        setLoadingDynamic(false);
      }
      return;
    }

    // Standard templates: prefill EVERY placeholder found in subject+body
    // (union with declared variables), then render.
    const names = Array.from(new Set([...(template.variables || []), ...extractPlaceholders(template)]));
    const initialVars = {};
    names.forEach((varName) => {
      initialVars[varName] = valueForVar(varName);
    });
    setVariables(initialVars);
    renderTemplate(template, initialVars);
  };

  // Resolve selected animations once allAnimations loads
  useEffect(() => {
    if (allAnimations.length > 0 && selectedAnimations.some(a => !a.resolved)) {
      const resolved = selectedAnimations.map(sel => {
        if (sel.resolved) return sel;
        const found = allAnimations.find(a =>
          a.name.toLowerCase().includes(sel.name.toLowerCase()) ||
          sel.name.toLowerCase().includes(a.name.toLowerCase())
        );
        if (found) {
          const pricing = found.pricing || {};
          const tierKeys = Object.keys(pricing);
          return {
            name: found.name,
            selected_tier_key: tierKeys[0] || null,
            resolved: true
          };
        }
        return { ...sel, resolved: true };
      });
      setSelectedAnimations(resolved);
    }
  }, [allAnimations]);

  const renderTemplate = (template, vars) => {
    let subject = template.subject;
    let body = template.body;
    Object.entries(vars).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      subject = subject.replace(regex, value || `[${key}]`);
      body = body.replace(regex, value || `[${key}]`);
    });
    setRenderedContent({ subject, body, bodyHtml: "" });
  };

  const handleVariableChange = (varName, value) => {
    const newVars = { ...variables, [varName]: value };
    setVariables(newVars);
    if (selectedTemplate) renderTemplate(selectedTemplate, newVars);
  };

  // Add animation from catalog
  const addAnimation = (anim) => {
    if (selectedAnimations.find(s => s.name === anim.name)) {
      toast.info("Animation deja selectionnee");
      return;
    }

    const pricing = anim.pricing || {};
    const tierKeys = Object.keys(pricing);
    const pricingType = detectPricingType(pricing);

    if (pricingType && tierKeys.length > 1) {
      // Ask the user to choose a tier
      setPricingQuestion({
        animation: anim,
        question: pricingType.label,
        tiers: tierKeys.map(k => ({ key: k, value: pricing[k] }))
      });
    } else {
      // Auto-select first tier
      setSelectedAnimations(prev => [...prev, {
        name: anim.name,
        selected_tier_key: tierKeys[0] || null,
        resolved: true
      }]);
    }
    setAnimSearch("");
  };

  // Confirm pricing choice from popup
  const confirmPricingChoice = (tierKey) => {
    if (!pricingQuestion) return;
    setSelectedAnimations(prev => [...prev, {
      name: pricingQuestion.animation.name,
      selected_tier_key: tierKey,
      resolved: true
    }]);
    setPricingQuestion(null);
  };

  // Change tier for existing selection
  const changeTier = (index, tierKey) => {
    setSelectedAnimations(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected_tier_key: tierKey };
      return updated;
    });
  };

  // Remove animation
  const removeAnimation = (index) => {
    setSelectedAnimations(prev => prev.filter((_, i) => i !== index));
  };

  // Generate email
  const generateEmail = async () => {
    if (selectedAnimations.length === 0) {
      toast.error("Selectionnez au moins une animation");
      return;
    }
    setLoadingDynamic(true);
    try {
      const res = await axios.post(`${apiUrl}/email-templates/generate-animation-links`, {
        animations_selection: selectedAnimations.map(a => ({
          name: a.name,
          selected_tier_key: a.selected_tier_key
        })),
        first_name: extractedData?.first_name || "Client"
      });
      setRenderedContent({
        subject: res.data.subject,
        body: res.data.body,
        bodyHtml: res.data.body_html || res.data.body,
      });
      setShowAnimSelector(false);
    } catch (e) {
      console.error("Error generating email:", e);
      toast.error("Erreur lors de la generation");
    } finally {
      setLoadingDynamic(false);
    }
  };

  const handleCopyToClipboard = async () => {
    // Build both MIME types so pasting into Outlook (rich text) preserves the
    // <a href> hyperlinks while pasting into a plain editor still works.
    const subject = renderedContent.subject || "";
    const bodyText = renderedContent.body || "";
    const bodyHtml = renderedContent.bodyHtml || bodyText;

    // For HTML version, ALWAYS convert plain-text newlines to <br /> (the
    // surrounding template body uses \n for paragraphs; only the animation
    // lines themselves are pre-wrapped in <p>...</p>). Outlook would otherwise
    // collapse all paragraphs on paste.
    const htmlPayload = `<div>${bodyHtml.replace(/\n/g, "<br />")}</div>`;
    const plainPayload = `Objet: ${subject}\n\n${bodyText}`;

    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([htmlPayload], { type: "text/html" }),
          "text/plain": new Blob([plainPayload], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        // Fallback for older browsers — plain text only
        await navigator.clipboard.writeText(plainPayload);
      }
      setCopied(true);
      toast.success("Email copié — collez-le dans Outlook pour préserver les liens cliquables");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Clipboard write failed", e);
      // Last-resort fallback
      try {
        await navigator.clipboard.writeText(plainPayload);
        setCopied(true);
        toast.success("Email copié (texte brut, hyperliens perdus)");
        setTimeout(() => setCopied(false), 2000);
      } catch (e2) {
        toast.error("Erreur lors de la copie");
      }
    }
  };

  const handleOpenMailClient = () => {
    const mailtoLink = `mailto:${senderEmail}?subject=${encodeURIComponent(renderedContent.subject)}&body=${encodeURIComponent(renderedContent.body)}`;
    window.open(mailtoLink, '_blank');
  };

  /**
   * Open Outlook's native reply form with the rendered HTML body pre-filled.
   * Outlook automatically quotes the original message in the thread (controlled
   * by the user's Outlook settings — typically "Include original message").
   */
  const handleReplyInOutlook = () => {
    const Office = window.Office;
    const item = Office?.context?.mailbox?.item;
    if (!item || typeof item.displayReplyForm !== "function") {
      toast.error("L'API Outlook n'est pas disponible dans ce contexte");
      return;
    }
    const bodyHtml = renderedContent.bodyHtml || renderedContent.body || "";
    // Normalize newlines for Outlook: outside any pre-existing <p>/<br>, treat
    // \n as paragraph breaks.
    const htmlPayload = `<div>${bodyHtml.replace(/\n/g, "<br />")}</div>`;
    try {
      item.displayReplyForm({ htmlBody: htmlPayload });
      toast.success("Brouillon de réponse ouvert dans Outlook");
    } catch (e) {
      console.error("displayReplyForm failed", e);
      toast.error("Impossible d'ouvrir la réponse Outlook");
    }
  };

  if (templates.length === 0) {
    return (
      <div className="empty-state">
        <Mail className="empty-state-icon mx-auto" />
        <p className="empty-state-title">Aucun modele disponible</p>
        <p className="empty-state-text">Les modeles d'email seront charges automatiquement.</p>
      </div>
    );
  }

  // Filter catalog animations for search
  const filteredAnimations = allAnimations.filter(a =>
    a.name.toLowerCase().includes(animSearch.toLowerCase()) &&
    !selectedAnimations.find(s => s.name === a.name)
  );

  return (
    <div className="space-y-2" data-testid="template-selector">
      {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => {
        const Icon = categoryIcons[category] || Mail;
        const label = categoryLabels[category] || category;

        return (
          <div key={category}>
            <div className="section-header">{label}</div>

            {categoryTemplates.map((template) => (
              <Dialog key={template.id}>
                <DialogTrigger asChild>
                  <button
                    onClick={() => handleSelectTemplate(template)}
                    className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-md transition-colors text-left mb-2"
                    data-testid={`template-${template.id}`}
                  >
                    <div className="w-8 h-8 rounded-md bg-white border border-slate-200 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{template.name}</p>
                      <p className="text-xs text-slate-500 truncate">{template.subject}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                </DialogTrigger>

                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-base">{template.name}</DialogTitle>
                    <DialogDescription className="text-xs">
                      Personnalisez les variables puis copiez ou répondez dans Outlook.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4">
                    {/* Animation Selector for animation_info templates */}
                    {template.category === "animation_info" && showAnimSelector && (
                      <div className="space-y-3" data-testid="animation-selector">
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          Animations selectionnees
                        </Label>

                        {/* Selected animations list */}
                        <div className="space-y-2">
                          {selectedAnimations.map((sel, idx) => {
                            const anim = allAnimations.find(a => a.name === sel.name);
                            const pricing = anim?.pricing || {};
                            const tierKeys = Object.keys(pricing);
                            const currentTarif = sel.selected_tier_key && pricing[sel.selected_tier_key]
                              ? pricing[sel.selected_tier_key]
                              : tierKeys.length > 0 ? pricing[tierKeys[0]] : "sur devis";

                            return (
                              <div key={idx} className="p-2.5 bg-slate-50 border border-slate-200 rounded-md" data-testid={`selected-anim-${idx}`}>
                                <div className="flex items-start gap-2">
                                  <Gamepad2 className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                      <p className="text-sm font-medium text-slate-900 truncate">{sel.name}</p>
                                      {anim?.link && (
                                        <a href={anim.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                                          <ExternalLink className="w-3 h-3" />
                                        </a>
                                      )}
                                    </div>

                                    {/* Tier selector */}
                                    {tierKeys.length > 1 ? (
                                      <Select
                                        value={sel.selected_tier_key || tierKeys[0]}
                                        onValueChange={(val) => changeTier(idx, val)}
                                      >
                                        <SelectTrigger className="h-7 text-xs mt-1 w-full">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {tierKeys.map(k => (
                                            <SelectItem key={k} value={k} className="text-xs">
                                              {k} — {pricing[k]}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <p className="text-xs text-slate-500 mt-0.5">
                                        Tarif : {currentTarif}
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => removeAnimation(idx)}
                                    className="text-slate-400 hover:text-rose-500 transition-colors"
                                    data-testid={`remove-anim-${idx}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Add from catalog */}
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <Input
                            type="text"
                            value={animSearch}
                            onChange={(e) => setAnimSearch(e.target.value)}
                            className="h-8 text-xs pl-8"
                            placeholder="Ajouter une animation du catalogue..."
                            data-testid="anim-search-input"
                          />
                        </div>

                        {animSearch && (
                          <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-md bg-white" data-testid="anim-search-results">
                            {filteredAnimations.length > 0 ? (
                              filteredAnimations.slice(0, 8).map(anim => (
                                <button
                                  key={anim.id}
                                  onClick={() => addAnimation(anim)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 text-xs border-b border-slate-100 last:border-0"
                                  data-testid={`add-anim-${anim.id}`}
                                >
                                  <Plus className="w-3 h-3 text-emerald-500" />
                                  <span className="flex-1 truncate font-medium">{anim.name}</span>
                                  <span className="text-slate-400 text-[10px]">{anim.category}</span>
                                </button>
                              ))
                            ) : (
                              <p className="p-2 text-xs text-slate-400 text-center">Aucun resultat</p>
                            )}
                          </div>
                        )}

                        {/* Pricing Question Popup */}
                        {pricingQuestion && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md space-y-2" data-testid="pricing-question">
                            <div className="flex items-center gap-2">
                              <HelpCircle className="w-4 h-4 text-amber-600" />
                              <p className="text-xs font-medium text-amber-800">
                                {pricingQuestion.animation.name}
                              </p>
                            </div>
                            <p className="text-xs text-amber-700">{pricingQuestion.question}</p>
                            <div className="space-y-1">
                              {pricingQuestion.tiers.map(tier => (
                                <button
                                  key={tier.key}
                                  onClick={() => confirmPricingChoice(tier.key)}
                                  className="w-full flex items-center justify-between px-3 py-1.5 bg-white border border-amber-200 rounded text-xs hover:bg-amber-100 transition-colors"
                                  data-testid={`pricing-option-${tier.key}`}
                                >
                                  <span className="font-medium">{tier.key}</span>
                                  <span className="text-slate-500">{tier.value}</span>
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={() => setPricingQuestion(null)}
                              className="text-[10px] text-amber-600 hover:underline"
                            >
                              Annuler
                            </button>
                          </div>
                        )}

                        {/* Generate button */}
                        <Button
                          onClick={generateEmail}
                          disabled={loadingDynamic || selectedAnimations.length === 0}
                          className="w-full h-8 text-xs bg-slate-900 hover:bg-slate-800"
                          data-testid="generate-anim-email-btn"
                        >
                          {loadingDynamic ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                          ) : (
                            <Gamepad2 className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          Generer l'email ({selectedAnimations.length} animation{selectedAnimations.length > 1 ? 's' : ''})
                        </Button>
                      </div>
                    )}

                    {/* Standard Variables for non-animation templates */}
                    {template.category !== "animation_info" && template.variables.length > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          Variables
                        </Label>
                        <div className="grid grid-cols-2 gap-2">
                          {template.variables.map((varName) => (
                            <div key={varName}>
                              <Label className="text-xs text-slate-600">{varName}</Label>
                              <Input
                                type="text"
                                value={variables[varName] || ''}
                                onChange={(e) => handleVariableChange(varName, e.target.value)}
                                className="h-8 text-sm"
                                placeholder={varName}
                                data-testid={`template-var-${varName}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Preview - only show when content is generated */}
                    {renderedContent.body && (!showAnimSelector || !template.category?.includes("animation")) && (
                      <div className="space-y-2">
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          Apercu
                        </Label>
                        <div className="bg-slate-50 rounded-md p-3 space-y-2">
                          <div>
                            <span className="text-xs text-slate-500">Objet :</span>
                            <p className="text-sm font-medium text-slate-900" data-testid="template-preview-subject">
                              {renderedContent.subject}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500">Corps :</span>
                            {renderedContent.bodyHtml ? (
                              <div
                                className="mt-1 text-sm min-h-[150px] bg-white border border-slate-200 rounded-md p-3 overflow-auto prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline [&_p]:my-1 whitespace-pre-wrap"
                                data-testid="template-preview-body-html"
                                dangerouslySetInnerHTML={{ __html: renderedContent.bodyHtml.replace(/\n/g, "<br />") }}
                              />
                            ) : (
                              <Textarea
                                value={renderedContent.body}
                                readOnly
                                className="mt-1 text-sm min-h-[150px] bg-white"
                                data-testid="template-preview-body"
                              />
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            onClick={handleCopyToClipboard}
                            variant="secondary"
                            className="flex-1 min-w-[100px] h-8 text-xs"
                            data-testid="copy-template-btn"
                          >
                            {copied ? (
                              <><Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> Copie !</>
                            ) : (
                              <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copier</>
                            )}
                          </Button>
                          {isOutlookContext ? (
                            <Button
                              onClick={handleReplyInOutlook}
                              className="flex-1 min-w-[140px] h-8 text-xs bg-blue-600 hover:bg-blue-700"
                              data-testid="reply-in-outlook-btn"
                            >
                              <Send className="w-3.5 h-3.5 mr-1.5" />
                              Répondre dans Outlook
                            </Button>
                          ) : (
                            <Button
                              onClick={handleOpenMailClient}
                              className="flex-1 min-w-[120px] h-8 text-xs bg-slate-900 hover:bg-slate-800"
                              data-testid="open-mail-client-btn"
                            >
                              <Send className="w-3.5 h-3.5 mr-1.5" />
                              Ouvrir dans Mail
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default TemplateSelector;
